import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { Button, Col, message, notification, PageHeader, Row, Space, Spin } from "antd";
import { defaultUserDataValue, IUserData } from "../../../../interfaces/userData.interface";
import { ApiClient } from "../../../../api-client/api.client";
import { ResetPasswordComponent } from "./reset-password.component";
import { ChangeEmailComponent } from "./change-email.component";
import { ActivatePatientAccount } from "./activate-patient-account";

interface IProps extends RouteComponentProps {}

interface IState {
  userData: IUserData;
  account: any;
  isLoading: {
    account: boolean;
    userData: boolean;
  };
}

export class EditAccountComponent extends React.Component<IProps, IState> {
  state: IState = {
    isLoading: {
      account: false,
      userData: false,
    },
    userData: defaultUserDataValue,
    account: undefined,
  };
  componentDidMount = () => {
    this.getUserData();
    this.getPatientAccount();
  };

  public async getPatientAccount() {
    try {
      this.setState({
        isLoading: { ...this.state.isLoading, account: true },
      });
      const { id } = this.props.match.params;
      const { data: account } = await ApiClient.getAccount(id);
      this.setState({
        account,
      });
    } catch {
      message.error("Patient account cannot be fetched!");
    } finally {
      this.setState({
        isLoading: { ...this.state.isLoading, account: false },
      });
    }
  }

  public getEmails(account: any): string[] {
    const emails = [];
    if (!account) return [];
    if (account.email) emails.push(account.email);
    if (account.secondaryEmail) emails.push(account.secondaryEmail);
    return emails;
  }

  private async switchPrimaryAndSecondaryEmail() {
    const { account } = this.state;
    if (!account) return;
    const { email, secondaryEmail } = account;
    if (!email || !secondaryEmail) return;
    this.setState({
      isLoading: { ...this.state.isLoading, account: true },
    });
    ApiClient.switchPrimaryAndSecondaryEmail(account.id, email, secondaryEmail)
      .then((_data) => {
        notification.success({
          message: "Switch primary and secondary email success",
        });
        this.getPatientAccount();
      })
      .catch((error) => {
        notification.error({
          message: "Switch primary and secondary email failed",
          description: `${JSON.stringify(error?.response?.data?.message || error.message)}`,
        });
        this.setState({
          isLoading: { ...this.state.isLoading, account: false },
        });
      });
  }
  private async removeSecondaryEmail() {
    const { account } = this.state;
    if (!account) return;
    if (!account.secondaryEmail) return;
    this.setState({
      isLoading: { ...this.state.isLoading, account: true },
    });
    ApiClient.removeSecondaryEmail(account.id)
      .then((_data) => {
        notification.success({
          message: "Secondary email removed successfully",
        });
        this.getPatientAccount();
      })
      .catch((error) => {
        notification.error({
          message: "Remove secondary email failed",
          description: `${JSON.stringify(error?.response?.data?.message || error.message)}`,
        });
        this.setState({
          isLoading: { ...this.state.isLoading, account: false },
        });
      });
  }

  render(): React.ReactNode {
    const { userData, account, isLoading } = this.state;
    const { id } = this.props.match.params;

    const hasAccessToResetPassword = userData.permissions.includes("RESET_PATIENT_PASSWORD");
    const hasAccessToChangeEmail = userData.permissions.includes("CHANGE_PATIENT_EMAIL");
    const hasAccessToActivatePatientAccount = userData.permissions.includes("ACTIVATE_PATIENT_ACCOUNT");

    if (isLoading.userData || isLoading.account) {
      return (
        <Space
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
          }}
          align="center"
          size="middle">
          <Spin size="large" tip="Loading..." />
        </Space>
      );
    }
    const emails = this.getEmails(account);
    const accountId = account?.id;
    const activeAccount = account?.isActive ?? false;

    return (
      <>
        <PageHeader
          onBack={() => this.props.history.push("/settings/patient-accounts")}
          ghost={false}
          style={{
            paddingLeft: 0,
            paddingRight: 0,
          }}
          title="Edit patient account"
        />
        <Row gutter={[24, 24]}>
          {hasAccessToChangeEmail && (
            <>
              <Col xs={24} md={24} lg={12}>
                <ChangeEmailComponent type="primary" id={id} currentEmail={account.email} />
              </Col>
              <Col xs={24} md={24} lg={12}>
                <ChangeEmailComponent type="secondary" id={id} currentEmail={account.secondaryEmail} />
                {account.secondaryEmail && (
                  <Button
                    loading={isLoading.account}
                    type="primary"
                    danger
                    onClick={() => {
                      if (confirm("Are you sure you want to switch primary and secondary email?")) {
                        this.switchPrimaryAndSecondaryEmail();
                      }
                    }}
                    style={{ marginTop: 10 }}>
                    Switch primary and secondary email
                  </Button>
                )}{" "}
                {account.secondaryEmail && (
                  <Button
                    loading={isLoading.account}
                    type="primary"
                    danger
                    onClick={() => {
                      if (confirm("Are you sure you want to REMOVE secondary email?")) {
                        this.removeSecondaryEmail();
                      }
                    }}
                    style={{ marginTop: 10 }}>
                    Remove secondary email
                  </Button>
                )}
              </Col>
            </>
          )}
          {hasAccessToResetPassword && (
            <Col xs={24} md={24} lg={12}>
              <ResetPasswordComponent emails={emails} />
            </Col>
          )}
          {hasAccessToActivatePatientAccount && accountId && (
            <Col xs={24} md={24} lg={12}>
              <ActivatePatientAccount accountId={accountId} emails={emails} active={activeAccount} />
            </Col>
          )}
        </Row>
      </>
    );
  }

  private async getUserData(): any {
    this.setState({
      isLoading: { ...this.state.isLoading, userData: true },
    });

    try {
      const {
        data: { data: userData },
      } = await ApiClient.getAccountsMe();
      this.setState({ userData });
    } catch (e) {
      notification.error({
        message: "Error",
        description: "Check authorization token!",
      });
    } finally {
      this.setState({
        isLoading: { ...this.state.isLoading, userData: false },
      });
    }
  }
}
